import type { Interceptor } from '@connectrpc/connect';
import { ConnectError, Code } from '@connectrpc/connect';
import { useAuthStore } from '../store/authStore';
import { refreshAccessToken } from './auth';

// Single in-flight refresh promise to prevent duplicate refresh requests
let refreshPromise: Promise<void> | null = null;

/**
 * Get the current access token from the auth store.
 * Useful for making authenticated fetch requests outside of ConnectRPC.
 */
export const getAccessToken = (): string | null => {
  return useAuthStore.getState().accessToken;
};

/**
 * Auth interceptor for ConnectRPC
 *
 * Responsibilities:
 * 1. Skip auth header for auth.v1.AuthService endpoints (prevent loops)
 * 2. Inject Authorization: Bearer <token> header for all other requests
 * 3. Proactive refresh: If token expires in < 5 minutes, refresh before request
 * 4. Reactive refresh: On 401 error, attempt token refresh once and retry request
 * 5. Single in-flight refresh promise (prevent duplicate refreshes)
 * 6. Call clearAuth() on refresh failure → triggers login redirect
 */
export const authInterceptor: Interceptor = (next) => async (req) => {
  const {
    accessToken,
    refreshToken,
    isTokenExpiringSoon,
    setTokens,
    clearAuth,
  } = useAuthStore.getState();

  // Skip auth header for auth service endpoints (prevent infinite loops)
  if (req.service.typeName === 'auth.v1.AuthService') {
    return next(req);
  }

  // Proactive refresh: If token expires in < 5 minutes, refresh before request
  if (accessToken && refreshToken && isTokenExpiringSoon()) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken(refreshToken)
        .then(({ accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn }) => {
          setTokens(newAccessToken, newRefreshToken, expiresIn);
        })
        .catch((error) => {
          console.error('Proactive token refresh failed:', error);
          clearAuth(); // Refresh failed, force re-login
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    // Wait for refresh to complete
    await refreshPromise;

    // Get updated token after refresh
    const { accessToken: updatedToken } = useAuthStore.getState();
    if (updatedToken) {
      req.header.set('Authorization', `Bearer ${updatedToken}`);
    }
  } else if (accessToken) {
    // Token is still valid, inject it
    req.header.set('Authorization', `Bearer ${accessToken}`);
  }

  // Execute the request
  try {
    return await next(req);
  } catch (error) {
    // Reactive refresh: Handle 401 Unauthenticated errors
    if (error instanceof ConnectError && error.code === Code.Unauthenticated) {
      const { refreshToken: currentRefreshToken } = useAuthStore.getState();

      // Attempt token refresh once
      if (currentRefreshToken && !refreshPromise) {
        try {
          refreshPromise = refreshAccessToken(currentRefreshToken)
            .then(({ accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn }) => {
              setTokens(newAccessToken, newRefreshToken, expiresIn);
            })
            .finally(() => {
              refreshPromise = null;
            });

          await refreshPromise;

          // Retry original request with new token
          const { accessToken: newToken } = useAuthStore.getState();
          if (newToken) {
            req.header.set('Authorization', `Bearer ${newToken}`);
            return await next(req);
          }
        } catch (refreshError) {
          console.error('Reactive token refresh failed:', refreshError);
          clearAuth(); // Refresh failed, force re-login
          throw error; // Re-throw original error to trigger login redirect
        }
      }

      // No refresh token or refresh already failed
      clearAuth();
    }

    // Re-throw all other errors
    throw error;
  }
};
